import * as React from "react";
import {
  Button,
  Divider,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions
} from "@mui/material";
import { abs } from "mathjs";
import { useDispatch, useSelector } from "react-redux";
import produce from "immer";
import StarsIcon from "@mui/icons-material/Stars";
import { tl, t } from "../../../components/translate";
import PercentFormat from "../../../components/FormattingInput/PercentInput";
import NrsFormat from "../../../components/FormattingInput/NrsInput";
import PaymentInfo from "./PaymentInfo";
import {
  BillDocumentType,
  DiscountBasedOn,
  DiscountTypes
} from "../../../interfaces/BillInterfaces";
import { RootState } from "../../../store";
import { resourceCentreActions } from "../../../actions";
import RemindOn from "../RemindOn";
import DiscountTypeSelect from "./DiscountTypeSelect";
import {
  deserializeNode,
  proseMirrorOptions,
  RichTextfield
} from "../../../components/RichTextfield";
import { useParsedProseMirror } from "../../Lab/LabEntry/LabTestSubRow";

// @ts-expect-error - css
import styles from "./BillEditor.module.css";
import MultiplePaymentMethodSelect from "./MultiplePaymentMethodSelect";

export enum paymentOptionsEnum {
  cash = "cash",
  card = "card",
  cheque = "cheque",
  onlinePayment = "onlinePayment",
  noPayment = "noPayment",
  balance = "balance",
  bankTransfer = "bankTransfer",
  khalti = "khalti",
  eSewa = "eSewa",
  fonePay = "fonePay",
  nepalPay = "nepalPay"
}

export const paymentOptions = [
  paymentOptionsEnum.cash,
  paymentOptionsEnum.card,
  paymentOptionsEnum.cheque,
  paymentOptionsEnum.onlinePayment,
  paymentOptionsEnum.noPayment,
  paymentOptionsEnum.balance,
  paymentOptionsEnum.bankTransfer,
  paymentOptionsEnum.khalti,
  paymentOptionsEnum.eSewa,
  paymentOptionsEnum.fonePay,
  paymentOptionsEnum.nepalPay
];

interface SummaryProps {
  draft: BillDocumentType;
  updateDraft: (draft: BillDocumentType) => void;
  isCreditNote: boolean;
}

const Summary = ({ draft, updateDraft, isCreditNote }: SummaryProps): React.ReactElement => {
  const dispatch = useDispatch();
  const resourceCentre =
    useSelector((state: RootState) => state.resources.resourceCentres[0]) || {};

  const handleDiscountTypeChange = (value) => {
    updateDraft(
      produce(draft, (draftItem) => {
        draftItem.settings.discountSettings.discountType = value;
      })
    );

    dispatch(
      resourceCentreActions.putResourceCentre(
        produce(resourceCentre, (draftItem) => {
          draftItem.settings.billingSettings.discountSettings.discountType = value;
        })
      )
    );
  };

  const [billRemarksState, setBillRemarksState] = useParsedProseMirror(
    draft?.remarks || "",
    proseMirrorOptions
  );

  React.useEffect(() => {
    updateDraft({
      ...draft,
      remarks: deserializeNode(billRemarksState.doc)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billRemarksState]);

  return (
    <Box sx={{ px: 2, py: 2, width: "100%", display: "flex", flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1 }}>
        <div>
          <RemindOn draft={draft} updateDraft={updateDraft} isCreditNote={isCreditNote} />
        </div>

        {/* <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="flex-end"
            flexWrap="wrap"
            width={0.7}
          >
            <Box flexBasis="200px" maxWidth="300px">
              <Typography>
                <Box component="span" fontWeight={500} marginBottom="8px">
                  {tl("billing.paymentMethod")}
                </Box>
              </Typography>
              <PaymentMethodSelect
                selectedPaymentMethod={selectedPaymentMethod}
                disabled={
                  draft?.isProformaBill ||
                  !enablePaymentSelection ||
                  (draft.paymentInfo.paymentMethod === paymentOptionsEnum.balance &&
                    !!draft.client.balance)
                }
                onPaymentMethodChange={(e, v) => {
                  if (!v) return;
                  updateDraft({
                    ...draft,
                    paymentInfo: {
                      ...draft.paymentInfo,
                      paymentMethod: v,
                      ...(v !== paymentOptionsEnum.cash &&
                        !isCreditNote && {
                          tenderAmount: 0,
                          changeAmount: 0
                        })
                    }
                  });
                }}
                isCreditNote={isCreditNote}
                paymentOptionsProp={
                  /**
                   * @todo need to revisit after handling payment through balance for invoice bill,
                   * since credit note's payment method too will have 'balance'
                   * @done temporarily solved by forcing payemnt method to be cash if its
                   * credit note, even if original bill utilized amount in 'balance' 
                  paymentMethodList
                }
              />
            </Box>
          </Box> */}

        <Box sx={{ width: { xs: "auto", md: 450 } }}>
          <MultiplePaymentMethodSelect isCreditNote={isCreditNote} />
        </Box>

        {draft.client?.associateCompany && (
          <Card sx={{ maxWidth: 450 }}>
            <CardContent
              sx={{
                backgroundColor: "#E1F5EE",
                display: "flex",
                p: 1,
                gap: 1,
                alignItems: "flex-start"
              }}
            >
              <StarsIcon color="primary" fontSize="large" />
              <Box flex={1}>
                <Typography variant="body1">
                  {draft.client?.associateCompany?.isMembership
                    ? `${draft.client?.associateCompany.name} Membership`
                    : draft.client?.associateCompany.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {`${draft.client?.associateCompany?.discountRate}% discount available`}
                </Typography>
              </Box>
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 2,
                  padding: "2px 10px"
                })}
              >
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.primary.contrastText
                  })}
                >
                  −{draft.client?.associateCompany?.discountRate}%
                </Typography>
              </Box>
              {/* <Chip color="primary" size="medium" label="-10%" /> */}
            </CardContent>
            <CardActions sx={{ justifyContent: "space-between" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Eligible for discount
              </Typography>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                sx={{ textTransform: "none" }}
                onClick={() => {
                  if (draft.settings.discountSettings.discountBasis === DiscountBasedOn.inline) {
                    const updated = draft.billItems.map((item) => ({
                      ...item,
                      discountPercent: draft.client?.associateCompany?.discountRate || 0
                    }));
                    updateDraft({
                      ...draft,
                      billItems: updated
                    });
                  } else {
                    updateDraft({
                      ...draft,
                      summary: {
                        ...draft.summary,
                        discountPct: Number(draft.client?.associateCompany?.discountRate),
                        discountType: "percent"
                      }
                    });
                  }
                }}
              >
                Appy Discount
              </Button>
            </CardActions>
          </Card>
        )}

        <Box>
          <Typography>
            <Box component="span" fontWeight={500} marginBottom="8px">
              {tl("billing.remarks")}
            </Box>
          </Typography>

          <Box sx={{ width: "450px" }}>
            <RichTextfield
              state={billRemarksState}
              setState={setBillRemarksState}
              minHeight={50}
              showBoldItalicUnderlineOnly
            />
          </Box>
        </Box>
      </Box>

      {/* Right Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box>
          {/* taxable sales */}
          {!!draft.summary.taxableSales && (
            <Box component="span" sx={{ display: "flex", mb: "8px" }}>
              <Typography component="span" width="100%" fontWeight={500}>
                Taxable Sales
              </Typography>
              <TextField
                size="small"
                value={draft.summary.taxableSales?.toFixed(2)}
                margin="dense"
                className={styles.nrsReadInputRoot}
                disabled
                slotProps={{
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: t("rs"),
                    classes: {
                      root: styles.nrsReadInputRoot,
                      input: styles.nrsReadInput
                    }
                  }
                }}
              />
            </Box>
          )}

          {/* exempt sales */}
          {!!draft.summary.exemptSales && (
            <Box component="span" sx={{ display: "flex", mb: "8px" }}>
              <Typography component="span" width="100%" fontWeight={500}>
                Exempt Sales
              </Typography>
              <TextField
                size="small"
                value={draft.summary.exemptSales.toFixed(2)}
                margin="dense"
                className={styles.nrsReadInputRoot}
                disabled
                slotProps={{
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: t("rs"),
                    classes: {
                      root: styles.nrsReadInputRoot,
                      input: styles.nrsReadInput
                    }
                  }
                }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {/* gross total amount */}
          <Box component="span" sx={{ display: "flex" }}>
            <Box component="span" display="inline-block" width="100%" fontWeight={500}>
              {tl("billing.grossTotalAmount")}
            </Box>
            <Box component="span" marginLeft="16px">
              <TextField
                size="small"
                value={draft.summary.totalPerUnitTimesQty?.toFixed(2) || 0}
                margin="dense"
                className={styles.nrsReadInputRoot}
                disabled
                slotProps={{
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: t("rs"),
                    classes: {
                      root: styles.nrsReadInputRoot,
                      input: styles.nrsReadInput
                    }
                  }
                }}
              />
            </Box>
          </Box>

          {/* discount */}
          <Box component="span" sx={{ display: "flex" }}>
            <Box
              component="span"
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              fontWeight={500}
            >
              {tl("billing.discount")}
            </Box>
            <Box component="span" marginLeft="16px">
              {draft.settings.discountSettings.discountType === DiscountTypes.percentage &&
              draft.settings.discountSettings.discountBasis === DiscountBasedOn.invoice ? (
                <TextField
                  disabled={
                    isCreditNote ||
                    draft.settings.discountSettings.discountBasis !== DiscountBasedOn.invoice
                  }
                  data-testmation="billing.discountPct"
                  value={draft.summary.discountPct}
                  margin="dense"
                  size="small"
                  variant={
                    draft.settings.discountSettings.discountBasis !== DiscountBasedOn.invoice
                      ? "standard"
                      : "outlined"
                  }
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    if (!isCreditNote)
                      updateDraft({
                        ...draft,
                        summary: {
                          ...draft.summary,
                          discountPct: Number(e.target.value) <= 100 ? Number(e.target.value) : 100,
                          discountType: "percent"
                        }
                      });
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      ...(isCreditNote && { disableUnderline: true }),
                      inputComponent: PercentFormat,
                      classes: { input: styles.discountBillInput },
                      endAdornment: "%",
                      startAdornment: (
                        <DiscountTypeSelect
                          disabled={isCreditNote}
                          fieldValue={draft.settings.discountSettings.discountType}
                          onChange={(value) => {
                            handleDiscountTypeChange(value);
                          }}
                        />
                      )
                    }
                  }}
                />
              ) : (
                <TextField
                  size="small"
                  disabled={
                    isCreditNote ||
                    draft.settings.discountSettings.discountBasis === DiscountBasedOn.inline
                  }
                  data-testmation="billing.discountAmount"
                  value={draft.summary.discount.toFixed(2)}
                  margin="dense"
                  variant={isCreditNote ? "standard" : "outlined"}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const maxAllowedDiscount = draft.summary.totalPerUnitTimesQty;
                    const discount = Number(e.target.value);
                    if (!isCreditNote)
                      updateDraft({
                        ...draft,
                        summary: {
                          ...draft.summary,
                          discount: discount <= maxAllowedDiscount ? discount : maxAllowedDiscount,
                          discountType: "amount"
                        }
                      });
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      ...(isCreditNote && { disableUnderline: true }),
                      inputComponent: NrsFormat,
                      classes: { input: styles.discountBillInput },
                      startAdornment: (
                        <DiscountTypeSelect
                          disabled={
                            isCreditNote ||
                            draft.settings.discountSettings.discountBasis === DiscountBasedOn.inline
                          }
                          fieldValue={draft.settings.discountSettings.discountType}
                          onChange={(value) => {
                            handleDiscountTypeChange(value);
                          }}
                        />
                      )
                    }
                  }}
                />
              )}
            </Box>
          </Box>

          {/* taxable amount */}
          <Box component="span" sx={{ display: "flex" }}>
            <Box component="span" display="inline-block" width="100%" fontWeight={500}>
              {tl("billing.taxableAmount")}
            </Box>
            <Box component="span" marginLeft="16px">
              <TextField
                size="small"
                value={draft.summary.taxableAmount.toFixed(2)}
                margin="dense"
                className={styles.nrsReadInputRoot}
                disabled
                slotProps={{
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: t("rs"),
                    classes: {
                      root: styles.nrsReadInputRoot,
                      input: styles.nrsReadInput
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box>
          {/* Overall Tax (VAT) */}
          <Box>
            <Typography sx={{ fontWeight: 600 }}>TAX Amount With Rates</Typography>
            <Box>
              {Object.keys(draft.summary.taxAmtWithRates || {})
                .filter((item) => Number(item) > 0)
                .map((vatPct, index) => {
                  const key = `${vatPct}-${index}`;
                  return (
                    <Box component="span" sx={{ display: "flex", mb: "8px" }} key={key}>
                      <Box component="span" display="inline-block" width="100%" fontWeight={500}>
                        {tl("billing.VAT")} {vatPct}%
                      </Box>
                      <Box component="span" marginLeft="16px" textAlign="left">
                        <TextField
                          size="small"
                          value={draft.summary.taxAmtWithRates[vatPct].toFixed(2)}
                          margin="dense"
                          className={styles.nrsReadInputRoot}
                          disabled
                          slotProps={{
                            input: {
                              inputComponent: NrsFormat,
                              startAdornment: t("rs"),
                              classes: {
                                root: styles.nrsReadInputRoot,
                                input: styles.nrsReadInput
                              }
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box>
          <Box>
            {Boolean(draft.summary.vatAmount) && (
              <Typography>
                <Box component="span" sx={{ display: "flex", mb: "8px" }}>
                  <Box component="span" display="inline-block" width="100%" fontWeight={500}>
                    {tl("billing.vatAmount")}
                  </Box>
                  <Box component="span" marginLeft="16px">
                    <TextField
                      size="small"
                      value={draft.summary.vatAmount.toFixed(2)}
                      margin="dense"
                      className={styles.nrsReadInputRoot}
                      disabled
                      slotProps={{
                        input: {
                          inputComponent: NrsFormat,
                          startAdornment: t("rs"),
                          classes: {
                            root: styles.nrsReadInputRoot,
                            input: styles.nrsReadInput
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Typography>
            )}
          </Box>

          <Box>
            {Boolean(draft.summary.taxAmount) && (
              <Typography>
                <Box component="span" sx={{ display: "flex", mb: "8px" }}>
                  <Box component="span" display="inline-block" width="100%" fontWeight={500}>
                    {tl("billing.taxAmount")}
                  </Box>
                  <Box component="span" marginLeft="16px">
                    <TextField
                      size="small"
                      value={draft.summary.taxAmount.toFixed(2)}
                      margin="dense"
                      className={styles.nrsReadInputRoot}
                      disabled
                      slotProps={{
                        input: {
                          inputComponent: NrsFormat,
                          startAdornment: t("rs"),
                          classes: {
                            root: styles.nrsReadInputRoot,
                            input: styles.nrsReadInput
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          textAlign="right"
          marginBottom="8px"
          justifyContent="space-between"
          width="100%"
        >
          <Typography style={{ fontWeight: 500 }}>{tl("stock.roundingOff")}</Typography>
          <Box component="span" marginLeft="16px" textAlign="right">
            <TextField
              size="small"
              disabled={isCreditNote}
              type="number"
              value={draft.summary?.roundOffAmt}
              onFocus={(e) => e.target.select()}
              variant="outlined"
              slotProps={{
                input: {
                  startAdornment: tl("rs"),
                  classes: { input: styles.nrsInput }
                }
              }}
              onChange={(e) => {
                const roundOffAmt = e.target.value;
                if (Number(roundOffAmt) >= -5 && Number(roundOffAmt) <= 5) {
                  const newState = produce(draft, (draftItem) => {
                    draftItem.summary.roundOffAmt = Number(roundOffAmt);
                  });

                  updateDraft(newState);
                }
              }}
            />
          </Box>
        </Box>

        <Divider />

        <Typography>
          <Box component="span" marginTop="8px" display="flex">
            <Box
              component="span"
              fontSize="0.83em"
              display="inline-block"
              width="150px"
              fontWeight={600}
            >
              {tl("billing.total")}
            </Box>
            <Box
              component="span"
              fontSize="0.83em"
              fontWeight={500}
              textAlign="right"
              width="220px"
            >
              <TextField
                size="small"
                value={draft.summary.totalAmount.toFixed(2)}
                margin="dense"
                className={styles.nrsReadInputRoot}
                disabled
                slotProps={{
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: t("rs"),
                    classes: {
                      root: styles.nrsReadInputRoot,
                      input: styles.nrsReadInput
                    }
                  },
                  htmlInput: { allowNegative: isCreditNote }
                }}
              />
            </Box>
          </Box>
        </Typography>

        <Box component="div" marginTop="8px">
          <Typography>
            <Box component="span" fontSize="0.8em" fontWeight={500}>
              {" "}
              {draft.summary.inWords}
            </Box>
          </Typography>
        </Box>

        {isCreditNote ? (
          <Box>
            <Divider />
            <Typography>
              {Object.keys(draft.refundInfo || {}).map((key, i) =>
                key === "previousPaymentMethod" ? null : (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box component="span" marginTop="8px" display="flex" key={i}>
                    <Box
                      component="span"
                      fontSize="0.83em"
                      display="inline-block"
                      width="150px"
                      fontWeight={600}
                    >
                      {tl(`billing.${key}`)}
                    </Box>
                    <Box
                      component="span"
                      fontSize="0.83em"
                      marginLeft="16px"
                      fontWeight={500}
                      textAlign="right"
                    >
                      <TextField
                        size="small"
                        value={draft.refundInfo[key].toFixed(2)}
                        margin="dense"
                        className={styles.nrsReadInputRoot}
                        disabled
                        slotProps={{
                          htmlInput: { allowNegative: isCreditNote },
                          input: {
                            inputComponent: NrsFormat,
                            startAdornment: t("rs"),
                            classes: {
                              root: styles.nrsReadInputRoot,
                              input: styles.nrsReadInput
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                )
              )}
            </Typography>
          </Box>
        ) : null}

        <Divider />

        <Box component="div" marginTop="8px">
          <PaymentInfo
            draft={
              isCreditNote
                ? {
                    ...draft,
                    paymentInfo: {
                      ...draft.paymentInfo,
                      paidAmount: abs(draft.paymentInfo.paidAmount)
                    }
                  }
                : draft
            }
            updateDraft={(value) => {
              // eslint-disable-next-line no-unused-expressions
              isCreditNote
                ? updateDraft({
                    ...value,
                    paymentInfo: {
                      ...value.paymentInfo,
                      paidAmount: -abs(value.paymentInfo.paidAmount)
                    }
                  })
                : updateDraft(value);
            }}
            isCreditNote={isCreditNote}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Summary;
